/**
 * Botão da paciente — `components/ui/buttons/Button.tsx` do mobile, em CSS.
 * Raio 16, alturas 40/48/56, texto 600 em 14/16/18. As cores da marca vêm
 * dos tokens (`wine[800]` → `--eva-brand`, `wine[200]` → `--eva-brand-2`).
 */
import { Loader } from '@mantine/core';
import type { CSSProperties } from 'react';

export type PatientButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
export type PatientButtonSize = 'small' | 'medium' | 'large';

export interface PatientButtonProps {
  title: string;
  variant?: PatientButtonVariant;
  size?: PatientButtonSize;
  loading?: boolean;
  /** Padrão `true`, como no mobile. */
  fullWidth?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  style?: CSSProperties;
}

const ALTURA: Record<PatientButtonSize, number> = { small: 40, medium: 48, large: 56 };
const PADDING_H: Record<PatientButtonSize, number> = { small: 16, medium: 20, large: 24 };
const FONTE: Record<PatientButtonSize, number> = { small: 14, medium: 16, large: 18 };

const VARIANTE: Record<PatientButtonVariant, CSSProperties> = {
  primary: { background: 'var(--eva-brand)', color: '#FFFFFF' },
  secondary: { background: 'var(--eva-brand-2)', color: 'var(--eva-brand)' },
  outline: { background: 'transparent', border: '2px solid var(--eva-brand)', color: 'var(--eva-brand)' },
  ghost: { background: 'transparent', color: 'var(--eva-brand)' },
};

export function PatientButton({
  title,
  variant = 'primary',
  size = 'large',
  loading = false,
  fullWidth = true,
  disabled = false,
  onClick,
  type = 'button',
  style,
}: PatientButtonProps) {
  const corDoLoader = variant === 'primary' ? '#FFFFFF' : 'var(--eva-brand)';
  return (
    <button
      type={type}
      className="eva-p-button-reset"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
        height: ALTURA[size],
        padding: `0 ${PADDING_H[size]}px`,
        width: fullWidth ? '100%' : undefined,
        opacity: disabled ? 0.6 : 1,
        fontWeight: 600,
        fontSize: FONTE[size],
        lineHeight: 1,
        whiteSpace: 'nowrap',
        ...VARIANTE[variant],
        ...style,
      }}
    >
      {loading ? <Loader size={18} color={corDoLoader} /> : title}
    </button>
  );
}
